import {useCallback, useEffect, useRef, useState} from "react";
import type {TFunction} from "i18next";
import type {AppNotificationCreateAttributes} from "../NotificationContext";
import {deviceLabel} from "./deviceLabels";
import {persistDevice, readStoredDevice, rememberedDevice} from "./deviceStorage";
import {DEVICE_TOAST_TTL_MS} from "./timings";

const useDeviceList = ({
  retractSpeakerTest, clearGeneration, createNotification, hideNotification, t,
}: {
  retractSpeakerTest: () => void
  clearGeneration: number
  createNotification: (attributes: AppNotificationCreateAttributes) => string
  hideNotification: (id: string) => void
  t: TFunction
}) => {
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([])
  const [microphones, setMicrophones] = useState<MediaDeviceInfo[]>([])
  const [speakers, setSpeakers] = useState<MediaDeviceInfo[]>([])

  // Lazy initialisers, so the FIRST capture asks for the restored device instead of capturing the
  // system default and correcting itself afterwards.
  const [selectedCamera, setSelectedCameraState] = useState<string | null>(() => readStoredDevice('cameraId'))
  const [selectedMicrophone, setSelectedMicrophoneState] = useState<string | null>(() => readStoredDevice('microphoneId'))
  const [selectedSpeaker, setSelectedSpeakerState] = useState<string | null>(() => readStoredDevice('speakerId'))

  // Self-initiated re-captures per kind, advanced by watchTrack's `ended` handler (RISKS.md/P1.26).
  const recaptureAttemptsRef = useRef({ video: 0, audio: 0 })

  // Storage records INTENT: only a user's pick may call these wrappers. Recovery uses the raw
  // setters, which share their type, or a browser block overwrites a real choice (RISKS.md/P2.53).
  const setSelectedCamera = useCallback((id: string | null) => {
    recaptureAttemptsRef.current.video = 0
    persistDevice('cameraId', id)
    setSelectedCameraState(id)
  }, [])
  const setSelectedMicrophone = useCallback((id: string | null) => {
    recaptureAttemptsRef.current.audio = 0
    persistDevice('microphoneId', id)
    setSelectedMicrophoneState(id)
  }, [])
  const setSelectedSpeaker = useCallback((id: string | null) => {
    persistDevice('speakerId', id)
    setSelectedSpeakerState(id)
  }, [])

  const selectedCameraRef = useRef<string | null>(selectedCamera)
  selectedCameraRef.current = selectedCamera

  const selectedMicrophoneRef = useRef<string | null>(selectedMicrophone)
  selectedMicrophoneRef.current = selectedMicrophone

  // Last-seen label of the selected microphone, so loadDevices can spot the OS re-pointing the
  // `'default'` alias at a different device (RISKS.md/P2.38). Not state: nothing renders it.
  const micLabelRef = useRef<{ deviceId: string, label: string } | null>(null)

  const selectedSpeakerRef = useRef<string | null>(selectedSpeaker)
  selectedSpeakerRef.current = selectedSpeaker

  // The toast names a device long after the run started, when `speakers` is a stale closure (P2.44).
  const speakersRef = useRef<MediaDeviceInfo[]>(speakers)
  speakersRef.current = speakers

  // Pairs a label with the id it belonged to; keep in step with micLabelRef (RISKS.md/P2.39).
  const speakerLabelRef = useRef<{ deviceId: string, label: string } | null>(null)

  // Whether the last enumeration failed, so the report below fires on the edge (RISKS.md/P2.15).
  const deviceListFailedRef = useRef(false)

  // RISKS.md/P2.50: `clearNotifications` wipes the toast but not the broken device list behind it,
  // which is exactly what leaving the meeting does — re-arm, or the green room shows empty pickers
  // with no explanation. A dismissal uses `hideNotification`, does not bump this, and still sticks.
  useEffect(() => { deviceListFailedRef.current = false }, [clearGeneration])

  const loadDevices = async () => {
    await navigator.mediaDevices.enumerateDevices()
      .then(availableDevices => {
        if (deviceListFailedRef.current) {
          deviceListFailedRef.current = false
          hideNotification('device-list-failed')
        }
        const videoDevices = availableDevices.filter(d => d.kind === 'videoinput')
        const audioDevices = availableDevices.filter(d => d.kind === 'audioinput')
        const outputDevices = availableDevices.filter(d => d.kind === 'audiooutput')

        setCameras(videoDevices)
        setMicrophones(audioDevices)
        setSpeakers(outputDevices)

        if (videoDevices.length > 0) {
          const rememberedCam = rememberedDevice(videoDevices, 'cameraId')
          if (rememberedCam && rememberedCam.deviceId !== selectedCameraRef.current) {
            setSelectedCameraState(rememberedCam.deviceId)
          } else if (!selectedCameraRef.current) {
            setSelectedCameraState(videoDevices[0].deviceId)
          } else {
            const search = videoDevices.find(c => c.deviceId === selectedCameraRef.current)
            if (!search) {
              // RISKS.md/P2.28: only announce a REAL swap. A block leaves devices listed with
              // `deviceId: ''`, so a blank id on the fallback target is the tell that nothing moved.
              const camFallback = videoDevices[0]
              if (camFallback.deviceId) {
                createNotification({
                  id: 'camera-fallback',
                  message: t('lobby.cameraFallback', { name: deviceLabel(camFallback, videoDevices) || t('lobby.systemDefault') }),
                  ttl: DEVICE_TOAST_TTL_MS,
                })
              }
              setSelectedCameraState(camFallback.deviceId)
            }
          }
        } else {
          setSelectedCameraState(null)
        }
        // RISKS.md/P1.14: mirrors the camera branch — re-select when the current microphone is no
        // longer listed, not only when nothing is selected. A block strips the ids, so a stale id
        // would otherwise stay selected and no capture would be re-attempted.
        if (audioDevices.length > 0) {
          const search = audioDevices.find(m => m.deviceId === selectedMicrophoneRef.current)
          // RISKS.md/P2.38 + P2.39: neither field alone works. The OS silently re-points Chrome's
          // `'default'` audioinput ALIAS at another microphone — same id, different device — so only
          // the label moves. But the label also moves when the user picks a device deliberately, and
          // calling that "not available" is false. Hence both, scoped to an UNCHANGED id. Skipped
          // while labels are empty, which is what they are before permission.
          if (search?.label && micLabelRef.current
            && search.deviceId === micLabelRef.current.deviceId
            && search.label !== micLabelRef.current.label) {
            // De-dupe is FIRST-wins: without this hide, an earlier worse-named toast wins (RISKS.md/P2.44).
            hideNotification('microphone-fallback')
            createNotification({
              id: 'microphone-fallback',
              // Name the device that took over, or the user is left guessing which one they are on.
              // `systemDefault` covers unavailable labels, so the sentence can never read "… will be
              // used instead." with a blank in it.
              message: t('lobby.microphoneFallback', { name: deviceLabel(search, audioDevices) || t('lobby.systemDefault') }),
              ttl: DEVICE_TOAST_TTL_MS,
            })
          }
          micLabelRef.current = search?.label ? { deviceId: search.deviceId, label: search.label } : null
          const rememberedMic = rememberedDevice(audioDevices, 'microphoneId')
          if (rememberedMic && rememberedMic.deviceId !== selectedMicrophoneRef.current) {
            // The only thing that notices the device is BACK: after a fallback the selection can sit
            // on an alias that still resolves, so the branch below never fires. Storage is intent, so
            // this cannot override a deliberate pick (RISKS.md/P2.53).
            setSelectedMicrophoneState(rememberedMic.deviceId)
          } else if (!selectedMicrophoneRef.current || !search) {
            const fallback = audioDevices.find(m => m.deviceId === 'default') ?? audioDevices[0]
            // Announce it: a silent swap leaves the user on another microphone, inaudible (RISKS.md/P2.38).
            if (selectedMicrophoneRef.current && fallback.deviceId) {
              hideNotification('microphone-fallback')
              createNotification({
                id: 'microphone-fallback',
                message: t('lobby.microphoneFallback', { name: deviceLabel(fallback, audioDevices) || t('lobby.systemDefault') }),
                ttl: DEVICE_TOAST_TTL_MS,
              })
            }
            setSelectedMicrophoneState(fallback.deviceId === 'default' ? 'default' : fallback.deviceId)
          }
        } else {
          setSelectedMicrophoneState(null)
        }
        // RISKS.md/P2.39: mirrors the microphone branch above — same alias trap, same id-scoped
        // comparison, so the reasoning lives there.
        if (outputDevices.length > 0) {
          const search = outputDevices.find(s => s.deviceId === selectedSpeakerRef.current)
          if (search?.label && speakerLabelRef.current
            && search.deviceId === speakerLabelRef.current.deviceId
            && search.label !== speakerLabelRef.current.label) {
            // De-dupe is FIRST-wins: without this hide, an earlier worse-named toast wins (RISKS.md/P2.44).
            hideNotification('speaker-fallback')
            retractSpeakerTest()
            createNotification({
              id: 'speaker-fallback',
              message: t('lobby.speakerFallback', { name: deviceLabel(search, outputDevices) || t('lobby.systemDefault') }),
              ttl: DEVICE_TOAST_TTL_MS,
            })
          }
          speakerLabelRef.current = search?.label ? { deviceId: search.deviceId, label: search.label } : null

          const rememberedSpk = rememberedDevice(outputDevices, 'speakerId')
          if (rememberedSpk && rememberedSpk.deviceId !== selectedSpeakerRef.current) {
            setSelectedSpeakerState(rememberedSpk.deviceId)
          } else if (selectedSpeakerRef.current && !search) {
            const fallback = outputDevices.find(s => s.deviceId === 'default') ?? null
            if (fallback?.deviceId) {
              hideNotification('speaker-fallback')
              retractSpeakerTest()
              createNotification({
                id: 'speaker-fallback',
                message: t('lobby.speakerFallback', { name: deviceLabel(fallback, outputDevices) || t('lobby.systemDefault') }),
                ttl: DEVICE_TOAST_TTL_MS,
              })
            }
            // null, never an arbitrary device: that would display a choice the user never made, and
            // it would overwrite a reset made by applySinkId on the next poll — a toast loop.
            setSelectedSpeakerState(fallback?.deviceId ?? null)
          } else if (!selectedSpeakerRef.current) {
            setSelectedSpeakerState(outputDevices.find(s => s.deviceId === 'default')?.deviceId ?? null)
          }
        } else {
          // Ambiguous: no device at all, or a browser declining to list them — `speakerDeviceMissing`
          // separates the two, so nothing is raised here (RISKS.md/P2.50).
          setSelectedSpeakerState(null)
          speakerLabelRef.current = null
          // The SUCCESS claim only — never `retractSpeakerTest()` here: the failure toasts REPORT an
          // empty list, so retracting them on every poll would kill them a tick after raising (P2.50).
          hideNotification('speaker-test')
        }
      })
      .catch(e => {
        console.error(e)
        // RISKS.md/P2.15: the lists keep their last values, so the pickers go on naming devices
        // nobody re-checked, and no predicate notices — `speakerDeviceMissing` needs a SUCCESSFUL
        // enumeration to arm. Edge-triggered: the settings panel re-runs this every 5s (P2.43).
        if (deviceListFailedRef.current) return
        deviceListFailedRef.current = true
        createNotification({
          id: 'device-list-failed',
          message: t('lobby.deviceListFailed'),
          persistent: true,
          actions: [{ label: t('lobby.retry'), onClick: () => { void loadDevices() } }],
        })
      })
  }

  useEffect(() => {
    // RISKS.md/P1.14: do NOT re-add a prompt-priming getUserMedia here — enableCamera and
    // enableMicrophone raise the prompt themselves, so it would queue a third permission dialog.
    loadDevices()

    // Catches a revocation that changes a selected deviceId's VALUE, but NOT an OS-level block,
    // which leaves the device enumerated under its real id — the provider's `watchTrack` is the
    // real backstop (RISKS.md/P2.34). Removed on unmount (RISKS.md/P1.6).
    const onDeviceChange = () => { loadDevices() }
    navigator.mediaDevices.addEventListener('devicechange', onDeviceChange)
    return () => navigator.mediaDevices.removeEventListener('devicechange', onDeviceChange)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    cameras, setCameras, microphones, setMicrophones, speakers, setSpeakers,
    selectedCamera, setSelectedCamera, setSelectedCameraState,
    selectedMicrophone, setSelectedMicrophone, setSelectedMicrophoneState,
    selectedSpeaker, setSelectedSpeaker, setSelectedSpeakerState,
    selectedSpeakerRef, speakersRef, recaptureAttemptsRef,
    loadDevices,
  }
}

export default useDeviceList
